import { ReactNode } from 'react'
import { FormGroup, Modal } from 'react-bootstrap'
import FormHandler from '../../utils/Form/FormHandler'
import Field from '../../utils/Form/Widgets/Field'
import { DateRangeInput } from '../../utils/Form/Widgets/DateRangeInput'
import SelectWidget from '../../utils/Form/Widgets/Select'
import Check from '../../utils/Form/Widgets/Check'
import Select from 'react-select'
import axios from 'axios'
import { companyMeterList, companySitesList, companyOptionsList } from '../charts/core/_requests'

type Props = {
  onHide: () => void
  show: boolean
  itemIdForUpdate?: string
  refreshFunction?: Function
}

interface AdvanceSettings {
  export_file_type: string // "excel", "csv"
  csv_delimeter?: string //";", ":" etc. --> export_file_type csv ise bu field kullanılır
  mail_notification?: boolean // yes, no
  mail_address?: string // system or custom mail --> mail notification yes ise bu field kullanılır
  group_by: string // "meter", "site", "company"
}

class DataExport {
  name?: string
  company?: number
  company_sites?: string[]
  meter_type?: string
  meter_ids?: string[]
  date_period?: string //last_week, last_month, last_year ve custom olarak ayarlanabilir
  start_date?: string // date_period custom ise bu field kullanılır
  end_date?: string // date_period custom ise bu field kullanılır
  status?: string //disabled olmalı, çünkü bu field backend tarafından setleniyor
  details?: string // disabled olmalı, çünkü bu field backend tarafından setleniyor
  advance_settings?: AdvanceSettings
  export_file_id?: number //disabled olmalı, çünkü bu field backend tarafından setleniyor
  created_by?: number //disabled olmalı, çünkü bu field backend tarafından setleniyor
}

class DataExportEditModal extends FormHandler {
  constructor(props: Props) {
    super(props)
    this.state = {
      entity: {
        ...(new DataExport() as any),
        meter_type: 'electricity',
        date_period: 'last_month',
      },
      companyOptions: [],
      companySiteOptions: [],
      meterOptions: [],
      allSitesSelected: false,
      allMetersSelected: false,
    } as any
  }

  componentDidMount(): void {
    super.componentDidMount()
    companyOptionsList().then((options) => {
      this.setState({ companyOptions: options } as any)
    })
    const state = this.state as any
    if (!state.entity?.date_period) {
      this.setState(
        (prev: any) => ({ entity: { ...prev.entity, date_period: 'last_month' } }),
        () => {
          this.applyPresetDates('last_month')
        }
      )
    }
    if (!state.entity || state.entity.meter_type !== 'electricity') {
      this.setState(
        (prev: any) => ({ entity: { ...prev.entity, meter_type: 'electricity' } }),
        () => {
          this.loadMeters()
        }
      )
    }
    this.setState((prev: any) => ({
      entity: {
        ...prev.entity,
        advance_settings: {
          export_file_type: prev.entity.advance_settings?.export_file_type || 'csv',
          csv_delimeter: prev.entity.advance_settings?.csv_delimeter || ',',
          group_by: prev.entity.advance_settings?.group_by || 'meter',
          mail_notification: prev.entity.advance_settings?.mail_notification || false,
          mail_address: prev.entity.advance_settings?.mail_address,
        },
      },
    }))
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<any>): void {
    super.componentDidUpdate(prevProps as any, prevState as any)
    const currEntity = (this.state as any).entity
    const prevCompany = (prevState as any)?.entity?.company
    const currCompany = (this.state as any)?.entity?.company
    if (currCompany && currCompany !== prevCompany) {
      this.loadCompanySites(currCompany)
      const isAdd = this.props.itemIdForUpdate === 'add'
      if (isAdd) {
        this.setState((prev: any) => ({
          meterOptions: [],
          entity: { ...prev.entity, meter_ids: [] },
        }))
      } else {
        this.setState({ meterOptions: [] } as any)
      }
    }
    if (currEntity && typeof (currEntity as any).advance_settings === 'string') {
      try {
        const parsed = JSON.parse((currEntity as any).advance_settings as any)
        this.setState((prev: any) => ({
          entity: { ...prev.entity, advance_settings: parsed },
        }))
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error parsing advance_settings:', e)
      }
    }
    if (currEntity && !currEntity.date_period) {
      this.setState(
        (prev: any) => ({ entity: { ...prev.entity, date_period: 'last_month' } }),
        () => {
          this.applyPresetDates('last_month')
        }
      )
    }
    if (currEntity && currEntity.meter_type !== 'electricity') {
      this.setState(
        (prev: any) => ({ entity: { ...prev.entity, meter_type: 'electricity' } }),
        () => {
          this.loadMeters()
        }
      )
    }
  }

  private getToday(): Date {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }

  private formatDate(d: Date): string {
    const yyyy = d.getFullYear()
    const mm = (d.getMonth() + 1).toString().padStart(2, '0')
    const dd = d.getDate().toString().padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  private applyPresetDates(period: string) {
    const today = this.getToday()
    let start: Date | null = null
    let end: Date | null = null
    if (period === 'last_week') {
      // last 7 days including today
      end = today
      start = new Date(today)
      start.setDate(start.getDate() - 6)
    } else if (period === 'last_month') {
      // previous full month
      const firstOfCurrent = new Date(today.getFullYear(), today.getMonth(), 1)
      end = new Date(firstOfCurrent)
      end.setDate(end.getDate() - 1)
      start = new Date(end.getFullYear(), end.getMonth(), 1)
    } else if (period === 'last_year') {
      // previous full year
      const year = today.getFullYear() - 1
      start = new Date(year, 0, 1)
      end = new Date(year, 11, 31)
    }
    if (start && end) {
      this.setState((prev: any) => ({
        entity: {
          ...prev.entity,
          start_date: this.formatDate(start as Date),
          end_date: this.formatDate(end as Date),
        },
      }))
    }
  }

  private computePresetDates(period: string): { start_date: string; end_date: string } | null {
    const today = this.getToday()
    let start: Date | null = null
    let end: Date | null = null
    if (period === 'last_week') {
      end = today
      start = new Date(today)
      start.setDate(start.getDate() - 6)
    } else if (period === 'last_month') {
      const firstOfCurrent = new Date(today.getFullYear(), today.getMonth(), 1)
      end = new Date(firstOfCurrent)
      end.setDate(end.getDate() - 1)
      start = new Date(end.getFullYear(), end.getMonth(), 1)
    } else if (period === 'last_year') {
      const year = today.getFullYear() - 1
      start = new Date(year, 0, 1)
      end = new Date(year, 11, 31)
    }
    if (!start || !end) return null
    return {
      start_date: this.formatDate(start),
      end_date: this.formatDate(end),
    }
  }

  onCompanySelectChange = async (option: any) => {
    const companyId = option ? option.value : null
    this.setState(
      (prev: any) => ({ entity: { ...prev.entity, company: companyId }, companySiteOptions: [] }),
      async () => {
        if (companyId) {
          await this.loadCompanySites(companyId)
        }
      }
    )
  }

  private normalizeToArray(value: any): string[] {
    if (Array.isArray(value)) return value as string[]
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed) return []
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) return parsed
      } catch (e) {
        // not JSON, fall through
      }
      if (trimmed.includes(','))
        return trimmed
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      return [trimmed]
    }
    return []
  }

  async loadCompanySites(companyId: number | string) {
    if (!companyId) {
      this.setState({ companySiteOptions: [], allSitesSelected: false })
      return
    }
    const options = await companySitesList({ companyId: Number(companyId) })
    const allIds = options.map((o) => o.value)
    const isAdd = this.props.itemIdForUpdate === 'add'
    this.setState(
      (prev: any) => {
        const currentSitesRaw: any = (prev.entity as any)?.company_sites
        const currentSites = this.normalizeToArray(currentSitesRaw)
        const nextSites = isAdd ? allIds : currentSites
        const allSelected = nextSites.length > 0 && nextSites.length === allIds.length
        return {
          companySiteOptions: options,
          allSitesSelected: allSelected,
          entity: { ...prev.entity, company_sites: nextSites },
        }
      },
      async () => {
        await this.loadMeters()
      }
    )
  }

  async loadMeters() {
    const state = this.state as any
    const entity = state.entity as DataExport
    if (!entity || !entity.meter_type || !state.companySiteOptions || !entity.company_sites) {
      this.setState({ meterOptions: [], allMetersSelected: false })
      return
    }
    const siteIds = this.normalizeToArray((entity as any).company_sites)
    const selectedSites = state.companySiteOptions.filter((o: any) => siteIds.includes(o.value))
    const options = await companyMeterList({
      siteId: selectedSites as any,
      meterType: entity.meter_type as any,
    })
    const allIds = options.map((o) => o.value)
    const isAdd = this.props.itemIdForUpdate === 'add'
    this.setState((prev: any) => {
      const currentMeterIdsRaw: any = (prev.entity as any)?.meter_ids
      const currentMeterIds = this.normalizeToArray(currentMeterIdsRaw)
      const nextMeterIds = isAdd ? allIds : currentMeterIds.filter((id) => allIds.includes(id))
      const allSelected = nextMeterIds.length > 0 && nextMeterIds.length === allIds.length
      return {
        meterOptions: options,
        allMetersSelected: allSelected,
        entity: { ...prev.entity, meter_ids: nextMeterIds },
      }
    })
  }

  onCompanyChange = async (event: any) => {
    this.handleInputChange(event)
    const companyId = event.target.value
    await this.loadCompanySites(companyId)
    // reset meters on company change
    this.setState((prev: any) => ({
      meterOptions: [],
      allMetersSelected: false,
      entity: { ...prev.entity, meter_ids: [] },
    }))
  }

  onSitesChange = async (values: any[]) => {
    const siteIds = (values || []).map((v: any) => v.value)
    this.setState((prev: any) => ({ entity: { ...prev.entity, company_sites: siteIds } }))
    const state = this.state as any
    if ((state.entity as any).meter_type) {
      await this.loadMeters()
    }
  }

  onToggleAllSites = async (checked: boolean) => {
    const options = (this.state as any).companySiteOptions || []
    if (checked) {
      const allIds = options.map((o: any) => o.value)
      this.setState((prev: any) => ({
        allSitesSelected: true,
        entity: { ...prev.entity, company_sites: allIds },
      }))
    } else {
      this.setState((prev: any) => ({
        allSitesSelected: false,
        entity: { ...prev.entity, company_sites: [] },
      }))
    }
    const state = this.state as any
    if ((state.entity as any).meter_type) {
      await this.loadMeters()
    }
  }

  onMeterTypeChange = async (option: any) => {
    const meterType = option?.value ?? null
    this.setState((prev: any) => ({ entity: { ...prev.entity, meter_type: meterType } }))
    if (meterType) {
      await this.loadMeters()
    } else {
      this.setState((prev: any) => ({
        meterOptions: [],
        allMetersSelected: false,
        entity: { ...prev.entity, meter_ids: [] },
      }))
    }
  }

  onMetersChange = (values: any[]) => {
    const meterIds = (values || []).map((v: any) => v.value)
    this.setState((prev: any) => ({ entity: { ...prev.entity, meter_ids: meterIds } }))
  }

  onToggleAllMeters = (checked: boolean) => {
    const options = (this.state as any).meterOptions || []
    if (checked) {
      const allIds = options.map((o: any) => o.value)
      this.setState((prev: any) => ({
        allMetersSelected: true,
        entity: { ...prev.entity, meter_ids: allIds },
      }))
    } else {
      this.setState((prev: any) => ({
        allMetersSelected: false,
        entity: { ...prev.entity, meter_ids: [] },
      }))
    }
  }

  onDatePeriodChange = (event: any) => {
    this.handleInputChange(event)
    const val = event.target.value
    if (val !== 'custom') {
      this.applyPresetDates(val)
    }
  }

  onCustomRangeChange = (event: any) => {
    const text: string = event.target.value || ''
    const [start, end] = text.split(' to ')
    if (start && end) {
      this.setState((prev: any) => ({
        entity: { ...prev.entity, start_date: start, end_date: end },
      }))
    }
  }

  getApiUrl(): string {
    return '/data_export/save/' + this.props.itemIdForUpdate
  }
  getSingleUrl(): string | null {
    if (this.props.itemIdForUpdate === 'add') {
      return null
    } else {
      return '/data_export/single/' + this.props.itemIdForUpdate
    }
  }

  getPostData(): object {
    const entity: any = { ...(this.state as any).entity }
    const period = entity.date_period
    if (['last_week', 'last_month', 'last_year'].includes(period)) {
      const range = this.computePresetDates(period)
      if (range) {
        entity.date_period = 'custom'
        entity.start_date = range.start_date
        entity.end_date = range.end_date
      }
    }
    return { data_export: entity }
  }
  render(): ReactNode {
    var state = this.state as any
    let title = 'Add Data Export'
    if (this.state.entity && this.props.itemIdForUpdate !== 'add') {
      title = state.entity?.status === 'completed' ? 'View Data Export' : 'Edit Data Export'
    }
    const readOnly = state.entity?.status === 'completed'
    return (
      <Modal
        id='kt_modal_create_company'
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered'
        size='lg'
        show={this.props.show}
        backdrop={true}
        onHide={this.props.onHide}
      >
        <form
          onSubmit={(event) => {
            this.handleSubmit(event).then((response) => {
              const id = response?.data?.data?.entity_id
              if (this.props.refreshFunction) {
                this.props.refreshFunction(id)
              }
              this.props.onHide()
            })
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Basic Selections */}
            <FormGroup className='mb-3'>
              <Field
                title='Name'
                className='form-control'
                type='text'
                name='name'
                required
                disabled={readOnly}
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.name}
              />
            </FormGroup>
            <FormGroup className='mb-3'>
              <label className='form-label'>Company</label>
              <Select
                isMulti={false}
                options={(this.state as any).companyOptions}
                value={
                  (this.state as any).entity.company
                    ? (this.state as any).companyOptions.find(
                        (o: any) => o.value === (this.state as any).entity.company
                      ) || null
                    : null
                }
                isDisabled={readOnly}
                onChange={this.onCompanySelectChange}
                classNamePrefix='react-select'
                className='w-100'
              />
            </FormGroup>

            {/* Sites with All toggle */}
            {state.companySiteOptions && state.companySiteOptions.length > 0 ? (
              <FormGroup>
                <div className='mb-3'>
                  <label className='form-label'>Sites</label>
                  <Select
                    isMulti
                    options={state.companySiteOptions}
                    value={this.normalizeToArray(state.entity.company_sites)
                      .map((id: string) =>
                        state.companySiteOptions.find((o: any) => o.value === id)
                      )
                      .filter(Boolean)}
                    isDisabled={readOnly}
                    onChange={(vals: any) => this.onSitesChange(vals as any[])}
                    classNamePrefix='react-select'
                    className='w-100'
                  />
                </div>
              </FormGroup>
            ) : (
              <></>
            )}

            {/* Meter type is fixed to Electricity and not selectable */}

            {/* Meters with All toggle */}
            {state.meterOptions && state.meterOptions.length > 0 ? (
              <FormGroup>
                <div className='mb-3'>
                  <label className='form-label'>Meters</label>
                  <Select
                    isMulti
                    options={state.meterOptions}
                    value={this.normalizeToArray(state.entity.meter_ids)
                      .map((id: string) => state.meterOptions.find((o: any) => o.value === id))
                      .filter(Boolean)}
                    isDisabled={readOnly}
                    onChange={(vals: any) => this.onMetersChange(vals as any[])}
                    classNamePrefix='react-select'
                    className='w-100'
                  />
                </div>
              </FormGroup>
            ) : (
              <></>
            )}

            {/* Date period presets */}
            <FormGroup className='mb-3'>
              <SelectWidget
                title='Date Period'
                name='date_period'
                options={[
                  { value: 'last_week', label: 'Last Week' },
                  { value: 'last_month', label: 'Last Month' },
                  { value: 'last_year', label: 'Last Year' },
                  { value: 'custom', label: 'Custom' },
                ]}
                multiple={false}
                defaultValue={state.entity.date_period ?? 'last_month'}
                disabled={readOnly}
                onChange={this.onDatePeriodChange}
              />
            </FormGroup>
            {state.entity.date_period === 'custom' ? (
              <FormGroup className='mb-3'>
                <DateRangeInput
                  title='Custom Range'
                  name='custom_range'
                  seperator={'to'}
                  placeholder={'Select date range'}
                  disabled={readOnly}
                  onChange={this.onCustomRangeChange}
                  ranges={[
                    {
                      startDate: state.entity.start_date
                        ? new Date(state.entity.start_date)
                        : undefined,
                      endDate: state.entity.end_date ? new Date(state.entity.end_date) : undefined,
                      key: 'selection',
                    },
                  ]}
                />
              </FormGroup>
            ) : (
              <></>
            )}
            {/* Download Settings */}
            <div className='rounded border p-3 mt-4'>
              <h6 className='mb-3'>Download Settings</h6>
              <div className='row g-3'>
                <FormGroup className='mb-3'>
                  <SelectWidget
                    title='Export File Type'
                    name='advance_settings.export_file_type'
                    options={[
                      { value: 'excel', label: 'Excel' },
                      { value: 'csv', label: 'CSV' },
                    ]}
                    multiple={false}
                    defaultValue={state.entity.advance_settings?.export_file_type ?? 'csv'}
                    disabled={readOnly}
                    onChange={(e: any) => {
                      const val = e.target.value
                      this.setState((prev: any) => ({
                        entity: {
                          ...prev.entity,
                          advance_settings: {
                            ...(prev.entity.advance_settings || {}),
                            export_file_type: val,
                          },
                        },
                      }))
                    }}
                  />
                </FormGroup>
                {state.entity.advance_settings?.export_file_type === 'csv' ? (
                  <FormGroup className='mb-3'>
                    <div className='d-flex align-items-center'>
                      <Field
                        title='CSV Delimiter'
                        className='form-control'
                        type='text'
                        name='advance_settings.csv_delimeter'
                        disabled={readOnly}
                        onInput={(event: any) => {
                          const val = event.target.value
                          this.setState((prev: any) => ({
                            entity: {
                              ...prev.entity,
                              advance_settings: {
                                ...(prev.entity.advance_settings || {}),
                                csv_delimeter: val,
                              },
                            },
                          }))
                        }}
                        defaultValue={state.entity.advance_settings?.csv_delimeter ?? ','}
                      />
                      <span className='ms-2 text-muted' title={'CSV delimiter info'}>
                        <i className='fa fa-info-circle'></i>
                      </span>
                    </div>
                    <div className='form-text mt-1'>
                      Use the character that separates columns in CSV
                    </div>
                  </FormGroup>
                ) : (
                  <></>
                )}
                <FormGroup className='mb-3'>
                  <hr className='my-2' />
                  <h6>Notification</h6>
                  <Check
                    title='Enable Notification'
                    name='advance_settings.mail_notification'
                    type='switch'
                    checked={state.entity.advance_settings?.mail_notification ? true : false}
                    options={['']}
                    disabled={readOnly}
                    onChange={(e: any) => {
                      const val = e.target.checked
                      this.setState((prev: any) => ({
                        entity: {
                          ...prev.entity,
                          advance_settings: {
                            ...(prev.entity.advance_settings || {}),
                            mail_notification: val,
                            ...(val ? {} : { mail_address: undefined }),
                          },
                        },
                      }))
                    }}
                  />
                </FormGroup>
                {state.entity.advance_settings?.mail_notification ? (
                  <div className='col-md-6'>
                    <Field
                      title='Mail Address'
                      className='form-control'
                      type='text'
                      name='advance_settings.mail_address'
                      placeholder='user@example.com'
                      disabled={readOnly}
                      onInput={(event: any) => {
                        const val = event.target.value
                        this.setState((prev: any) => ({
                          entity: {
                            ...prev.entity,
                            advance_settings: {
                              ...(prev.entity.advance_settings || {}),
                              mail_address: val,
                            },
                          },
                        }))
                      }}
                      defaultValue={state.entity.advance_settings?.mail_address}
                    />
                    <div className='form-check mt-2'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='useSystemMail'
                        disabled={readOnly}
                        onChange={(e) => {
                          if (e.target.checked) {
                            this.setState((prev: any) => ({
                              entity: {
                                ...prev.entity,
                                advance_settings: {
                                  ...(prev.entity.advance_settings || {}),
                                  mail_address: 'system',
                                },
                              },
                            }))
                          }
                        }}
                      />
                      <label className='form-check-label' htmlFor='useSystemMail'>
                        Use system mail
                      </label>
                    </div>
                  </div>
                ) : (
                  <></>
                )}
                <div className='col-md-6'>
                  <SelectWidget
                    title='Group By'
                    name='advance_settings.group_by'
                    options={[
                      { value: 'meter', label: 'Meter' },
                      { value: 'site', label: 'Site' },
                      { value: 'company', label: 'Company' },
                    ]}
                    multiple={false}
                    defaultValue={state.entity.advance_settings?.group_by ?? 'meter'}
                    disabled={readOnly}
                    onChange={(e: any) => {
                      const val = e.target.value
                      this.setState((prev: any) => ({
                        entity: {
                          ...prev.entity,
                          advance_settings: {
                            ...(prev.entity.advance_settings || {}),
                            group_by: val,
                          },
                        },
                      }))
                    }}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className='justify-content-end'>
            {this.props.itemIdForUpdate !== 'add' ? (
              <button
                type='button'
                onClick={(event) => {
                  this.handleDelete(event).then((response) => {
                    if (this.props.refreshFunction) {
                      this.props.refreshFunction('')
                    }
                  })
                }}
                className='btn btn-danger btn-sm'
              >
                <span className='fa fa-trash'></span>
                Delete
              </button>
            ) : (
              <></>
            )}
            {state.entity?.status !== 'completed' ? (
              <>
                <button type='submit' className='btn btn-success btn-sm me-2'>
                  Save
                </button>
                <button
                  type='button'
                  className='btn btn-primary btn-sm'
                  onClick={async (e) => {
                    e.preventDefault()
                    try {
                      const saveResponse = await this.handleSubmit({
                        preventDefault: () => {},
                      } as any)
                      const id =
                        (saveResponse.data?.data?.entity_id as any) || this.props.itemIdForUpdate
                      if (id) {
                        await axios.post(`/data_export/run/${id}`)
                      }
                      if (this.props.refreshFunction) {
                        this.props.refreshFunction(id)
                      }
                      this.props.onHide()
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error('Save and Run failed', err)
                    }
                  }}
                >
                  Save and Run
                </button>
              </>
            ) : (
              <></>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    )
  }
}

export { DataExportEditModal }
